import React from "react";
import LMTable from "../Tables/LMTable";
import {LMColumn} from "../Tables/types";
import {Box, IconButton, Tooltip, Typography} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import {DbCustomer} from "sharedlibs";
import {useTranslation} from "react-i18next";
import {compareCustomerNumbers, CUSTOMER_NUMBER_SORT_MODEL} from "../../utils/customerNumberSort";

export interface AssignedCustomersTableProps {
  customers: DbCustomer[];
  loading: boolean;
  onRemove: (customer: DbCustomer) => void;
  removeDisabled?: boolean;
}

export default function AssignedCustomersTable({
  customers,
  loading,
  onRemove,
  removeDisabled = false,
}: Readonly<AssignedCustomersTableProps>) {
  const {t} = useTranslation();

  const columns: LMColumn<DbCustomer>[] = [
    {
      field: "name",
      headerName: "objects.customer.customerName",
      flex: 2,
    },
    {
      field: "customerNumber",
      headerName: "objects.customer.customerNumber",
      flex: 1,
      sortComparator: compareCustomerNumbers,
    },
    {
      field: "city",
      headerName: "objects.address.location",
      flex: 1,
      valueGetter: (_value, row) => row.address?.city ?? "",
    },
    {
      field: "doctorName",
      headerName: "objects.customer.doctorName",
      flex: 1.5,
    },
    {
      field: "_remove",
      headerName: "",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      width: 64,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Tooltip title={t("salesperson.removeCustomer")}>
          <span>
            <IconButton
              size="small"
              aria-label={t("salesperson.removeCustomer")}
              disabled={removeDisabled}
              onClick={() => onRemove(params.row as DbCustomer)}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
  ];

  if (!loading && customers.length === 0) {
    return (
      <Box sx={{p: 3, textAlign: "center", color: "text.secondary"}}>
        <Typography variant="body2">
          {t("salesperson.noCustomersAssigned")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{width: "100%"}}>
      <LMTable<DbCustomer>
        rows={customers}
        getRowId={(row) => row.id ?? ""}
        columns={columns}
        loading={loading}
        selectionMode="none"
        pageSizeOptions={[10, 25, 50]}
        options={{sortModel: CUSTOMER_NUMBER_SORT_MODEL}}
      />
    </Box>
  );
}
